import {
  Component, Prop, h, Host, State,
} from '@stencil/core';
import { IHeading, ICopy } from '../interfaces/interfaces';

@Component({
  tag: 'g-video-banner',
  styleUrl: 'g-video-banner.scss',
})

export class GVideoBanner {
  /**
   * Autoplay video when entering the page
   */
  @Prop() autoplay: boolean = false;

  /**
   * Add controls on the video
   */
  @Prop() controls: boolean = true;

  /**
   * If muted is set to false, captions are needed
   */
  @Prop() captions?: string;

  /**
   * Captions language
   */
  @Prop() captionsLang?: string;

  /**
   * Loop video after is done
   */
  @Prop() loop: boolean = false;

  /**
   * Unmute/mute video
   */
  @Prop() muted: boolean = true;

  /**
   * Preload video, possible values:
   * none: Indicates that the video should not be preloaded.
   * metadata: Indicates that only video metadata (e.g. length) is fetched.
   * auto: Indicates that the whole video file can be downloaded,
   * even if the user is not expected to use it.
   */
  @Prop() preload: 'none' | 'metadata' | 'auto' = 'metadata';

  /**
   * Don't open fullscreen mode when video is played
   */
  @Prop() playsinline: boolean = true;

  /**
   * Play button color for youtube video
   */
  @Prop() playButtonColor: 'white' | 'gray' = 'white';

  /**
   * Play button text
   */
  @Prop() playButtonText: string = 'Play Button';

  /**
   * Video source
   */
  @Prop() source: string;

  /**
   * Show an image while video is loading
   */
  @Prop() image: any;

  /**
   * JavaScript object used to set styles on the component
   */
  @Prop() styleObject?: any;

  /**
   * JavaScript object used to set heading data
   */
  @Prop() heading: IHeading;

  /**
   * Sets heading position relative to the video
   */
  @Prop() headingPosition: 'top' | 'bottom' = 'top';

  /**
   * JavaScript object used to set heading data
   */
  @Prop() copy: ICopy;

  /**
   * Sets heading position relative to the video
   */
  @Prop() copyPosition: 'top' | 'bottom' = 'top';

  /**
   * JavaScript object used to set heading data
   */
  @Prop() youtubeVideo: boolean = false;

  /**
   * JavaScript object used to set heading data
   */
  @Prop() mediaTitle: string;

  /**
   * Sets single-video class if there's only one single video reference in the container
   */
  @Prop() singleVideo: boolean = true;

  @State() isPlayed = false;

  private renderHeading() {
    return (
      <g-heading
        class={this.copy ? 'heading' : 'heading-only'}
        theme={this.heading.theme}
        text-align={this.heading.textAlign}
        content={this.heading.content}
        heading-size={this.heading.size}
      />
    );
  }

  private playVideo(e) {
    if (e) e.stopPropagation();
    this.isPlayed = !this.isPlayed;
    return this.isPlayed;
  }

  private youtubeUrl(source) {
    const url = new URL(source);
    if (!url.searchParams.has('autoplay')) {
      url.searchParams.append('autoplay', '1');
    }

    return url.toString();
  }

  private renderYoutubeVideo(source) {
    const videoContainer = {
      'g__video-banner__container': true,
      'single-video': this.singleVideo,
    };
    return (
      <div
        class={videoContainer}
        role="button"
        tabIndex={0}
        onClick={(e: UIEvent) => this.playVideo(e)}
        onKeyUp={(e: UIEvent) => this.playVideo(e)}
      >
        {!this.isPlayed && this.image?.largeImageUrl && (
          <figure
            class="g__video-banner__wrapper"
          >
            <picture class="g__video-banner-image">
              <source
                media="(max-width: 768px)"
                srcSet={this.image?.smallImageUrl}
              />
              <source
                media="(max-width: 1023px)"
                srcSet={this.image?.mediumImageUrl}
              />
              <source
                media="(min-width: 1024px)"
                srcSet={this.image?.largeImageUrl}
              />
              <img
                class="g__video-banner-image"
                src={this.image.largeImageUrl}
                alt={this.image.altText}
              />
            </picture>
            <svg
              class={`g__video-banner-play g__video-banner-play-${this.playButtonColor}`}
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 144 144"
            >
              <title>{ this.playButtonText }</title>
              <path d="M121.5 72l-79 45.2V26.8z" />
              <circle
                cx="72"
                cy="72"
                r="66.3"
                fill="none"
                stroke-width="10"
                stroke-miterlimit="10"
              />
            </svg>
          </figure>
        )}

        {this.isPlayed && (
          <div>
            <svg
              class="g__video-banner-play-close"
              version="1.1"
              xmlns="http://www.w3.org/2000/svg"
            >
              <line
                x1="1"
                y1="30"
                x2="30"
                y2="1"
                stroke="white"
                stroke-width="3"
              />
              <line
                x1="1"
                y1="1"
                x2="30"
                y2="30"
                stroke="white"
                stroke-width="3"
              />
            </svg>
            <iframe
              class="g__video-banner--iframe"
              title={this.mediaTitle}
              src={this.youtubeUrl(source)}
              allow="autoplay"
              frameborder="0"
              allowfullScreen
            />
          </div>
        )}
      </div>
    );
  }

  private renderCopy() {
    return (
      <g-copy
        class="copy"
        theme={this.copy.theme}
        type={this.copy.type}
        text-align={this.copy.textAlign}
        content={this.copy.content}
        link-color={this.copy.linkColor}
      />
    );
  }

  render() {
    const baseClass = 'g__video-banner';
    const contentClass = `${baseClass}__content`;
    const videoClass = `${baseClass}__video`;
    const videoContainer = {
      'g__video-banner__container': true,
      'single-video': this.singleVideo,
    };
    const supportedFormats = ['mp4', 'webm', 'ogg'];

    const specifiedType = this.source?.split('.').pop();

    // if type is not specified in source video, set default as mp4
    const type = supportedFormats.includes(specifiedType) ? specifiedType : 'mp4';
    const videoSource = {
      source: this.source,
      type,
    };

    return (
      <Host>
        {this.source && (
          <div
            class={contentClass}
            style={this.styleObject || null}
          >
            {this.heading?.content && this.headingPosition === 'top' && this.renderHeading()}
            {this.copy?.content && this.copyPosition === 'top' && this.renderCopy()}
            {!this.youtubeVideo && (
              <div
                class={videoContainer}
              >
                <video
                  data-testid={videoClass}
                  class={videoClass}
                  autoplay={this.autoplay}
                  controls={this.controls}
                  loop={this.loop}
                  muted={this.muted}
                  preload={this.preload}
                  poster={!this.isPlayed ? this.image.largeImageUrl : ''}
                  playsinline={this.playsinline}
                >
                  <source src={videoSource.source} type={`video/${videoSource.type}`} />
                  <track
                    kind="captions"
                    srclang={this.captionsLang}
                    src={this.captions}
                  />
                </video>
              </div>
            )}
            {this.youtubeVideo && this.renderYoutubeVideo(videoSource.source)}
            {this.heading && this.headingPosition === 'bottom' && this.renderHeading()}
            {this.copy && this.copyPosition === 'bottom' && this.renderCopy()}
          </div>
        )}
      </Host>
    );
  }
}
